'use client';
import { useEffect } from 'react';
import { Box, Typography, Button, Container, Card, CardContent, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, LinearProgress } from '@mui/material';
import { AccessTime, TrendingUp, Calculate, Speed, Savings, Person, Work, Assessment } from '@mui/icons-material';
import Header from '../components/HomepageContent/Header';
import Footer from '../components/HomepageContent/Footer';
import Link from 'next/link';

export default function TimeSavings() {
  useEffect(() => {
    document.title = 'CareAscendium - Time Savings';
  }, []);
  const timeSavings = [
    { task: "Documentation & Charting", current: "15 hours/week", automated: "5 hours/week", savings: "10 hours/week", annual: "520 hours/year", efficiency: 67 },
    { task: "Audit Preparation", current: "8 hours/month", automated: "2 hours/month", savings: "6 hours/month", annual: "72 hours/year", efficiency: 75 },
    { task: "Incident Reporting", current: "45 minutes/incident", automated: "15 minutes/incident", savings: "30 minutes/incident", annual: "40 hours/year", efficiency: 67 },
    { task: "Care Plan Updates", current: "3 hours/week", automated: "1 hour/week", savings: "2 hours/week", annual: "104 hours/year", efficiency: 67 },
    { task: "Compliance Monitoring", current: "4 hours/week", automated: "1 hour/week", savings: "3 hours/week", annual: "156 hours/year", efficiency: 75 },
    { task: "Staff Training Tracking", current: "2 hours/week", automated: "0.5 hours/week", savings: "1.5 hours/week", annual: "78 hours/year", efficiency: 75 },
    { task: "Quality Metrics Reporting", current: "6 hours/month", automated: "1 hour/month", savings: "5 hours/month", annual: "60 hours/year", efficiency: 83 },
    { task: "Family Communication", current: "2 hours/week", automated: "0.5 hours/week", savings: "1.5 hours/week", annual: "78 hours/year", efficiency: 75 }
  ];

  const totalAnnualSavings = timeSavings.reduce((total, item) => {
    const annualHours = parseInt(item.annual.split(' ')[0]);
    return total + annualHours;
  }, 0);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      {/* Top Navigation Bar */}
      <Header />

      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #153C7F 0%, #1E4A8C 50%, #2A5A9C 100%)',
        py: 12, 
        px: 4,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)',
          backgroundSize: '50px 50px'
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
            <Speed sx={{ fontSize: 64, color: 'white', mr: 3 }} />
            <Typography 
              variant="h1" 
              sx={{ 
                color: 'white', 
                mb: 4,
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Time Savings Calculator
            </Typography>
          </Box>
          <Typography 
            variant="h5" 
            sx={{ 
              color: 'rgba(255,255,255,0.9)',
              maxWidth: '900px',
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.2rem', md: '1.5rem' }
            }}
          >
            See exactly how much time CareAscendium can save your facility through intelligent automation and streamlined workflows
          </Typography>
        </Container>
      </Box>

      {/* Quick Stats Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(21,60,127,0.1)'
              }}
            >
              <AccessTime sx={{ fontSize: 48, color: '#153C7F', mb: 2 }} />
              <Typography variant="h3" sx={{ color: '#153C7F', fontWeight: 700, mb: 1 }}>
                {totalAnnualSavings}
              </Typography>
              <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>
                Hours Saved Annually
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(21,60,127,0.1)'
              }}
            >
              <Person sx={{ fontSize: 48, color: '#28a745', mb: 2 }} />
              <Typography variant="h3" sx={{ color: '#28a745', fontWeight: 700, mb: 1 }}>
                {Math.round(totalAnnualSavings / 40)}
              </Typography>
              <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>
                Staff Weeks Freed
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(21,60,127,0.1)'
              }}
            >
              <Savings sx={{ fontSize: 48, color: '#ff6b35', mb: 2 }} />
              <Typography variant="h3" sx={{ color: '#ff6b35', fontWeight: 700, mb: 1 }}>
                ${(totalAnnualSavings * 25).toLocaleString()}
              </Typography>
              <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>
                Annual Cost Savings
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(21,60,127,0.1)'
              }}
            >
              <TrendingUp sx={{ fontSize: 48, color: '#9c27b0', mb: 2 }} />
              <Typography variant="h3" sx={{ color: '#9c27b0', fontWeight: 700, mb: 1 }}>
                73%
              </Typography>
              <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>
                Average Efficiency Gain
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Detailed Analysis Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <Assessment sx={{ fontSize: 48, color: '#153C7F', mr: 2 }} />
            <Typography variant="h2" sx={{ color: '#153C7F', fontWeight: 700 }}>
              Detailed Time Savings Analysis
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
            Comprehensive breakdown of time savings across all major facility operations with efficiency metrics
          </Typography>
        </Box>

        <Paper 
          elevation={8}
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid rgba(21,60,127,0.1)'
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  background: 'linear-gradient(45deg, #153C7F, #1E4A8C)',
                  '& th': { 
                    color: 'white', 
                    fontWeight: 700,
                    fontSize: '1rem',
                    padding: 3
                  }
                }}>
                  <TableCell>Task</TableCell>
                  <TableCell align="center">Current Time</TableCell>
                  <TableCell align="center">With CareAscendium</TableCell>
                  <TableCell align="center">Time Saved</TableCell>
                  <TableCell align="center">Annual Savings</TableCell>
                  <TableCell align="center">Efficiency Gain</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timeSavings.map((saving, index) => (
                  <TableRow 
                    key={index} 
                    sx={{ 
                      '&:nth-of-type(odd)': { bgcolor: '#f8f9fa' },
                      '&:hover': { bgcolor: '#e3f2fd' },
                      transition: 'background-color 0.3s ease'
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {saving.task}
                    </TableCell>
                    <TableCell align="center" sx={{ color: '#666' }}>
                      {saving.current}
                    </TableCell>
                    <TableCell align="center" sx={{ color: '#28a745', fontWeight: 600 }}>
                      {saving.automated}
                    </TableCell>
                    <TableCell align="center" sx={{ color: '#153C7F', fontWeight: 700 }}>
                      {saving.savings}
                    </TableCell>
                    <TableCell align="center" sx={{ color: '#dc3545', fontWeight: 700 }}>
                      {saving.annual}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={saving.efficiency} 
                          sx={{ 
                            width: 60, 
                            height: 8, 
                            borderRadius: 4,
                            bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: '#28a745'
                            }
                          }} 
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#28a745' }}>
                          {saving.efficiency}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>

      {/* Impact Visualization */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)',
        py: 10 
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ color: '#153C7F', mb: 4, fontWeight: 700 }}>
              What You Can Do With Saved Time
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333' }}>
              Redirect valuable staff time to activities that directly improve resident care and facility operations
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper 
                elevation={6}
                sx={{ 
                  p: 4, 
                  height: '100%', 
                  textAlign: 'center',
                  borderRadius: 3,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  border: '1px solid rgba(21,60,127,0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <Person sx={{ fontSize: 64, color: '#28a745', mb: 3 }} />
                <Typography variant="h4" sx={{ color: '#153C7F', mb: 3, fontWeight: 700 }}>
                  Enhanced Resident Care
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.8, fontSize: '1.1rem' }}>
                  More time for direct resident interaction, personalized care planning, family communication, and quality of life activities
                </Typography>
              </Paper>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper 
                elevation={6}
                sx={{ 
                  p: 4, 
                  height: '100%', 
                  textAlign: 'center',
                  borderRadius: 3,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  border: '1px solid rgba(21,60,127,0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <Work sx={{ fontSize: 64, color: '#ff6b35', mb: 3 }} />
                <Typography variant="h4" sx={{ color: '#153C7F', mb: 3, fontWeight: 700 }}>
                  Staff Development
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.8, fontSize: '1.1rem' }}>
                  Invest in training, professional development, team building activities, and career advancement opportunities
                </Typography>
              </Paper>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper 
                elevation={6}
                sx={{ 
                  p: 4, 
                  height: '100%', 
                  textAlign: 'center',
                  borderRadius: 3,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  border: '1px solid rgba(21,60,127,0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <Assessment sx={{ fontSize: 64, color: '#9c27b0', mb: 3 }} />
                <Typography variant="h4" sx={{ color: '#153C7F', mb: 3, fontWeight: 700 }}>
                  Quality Improvement
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.8, fontSize: '1.1rem' }}>
                  Focus on quality initiatives, process improvements, innovation projects, and evidence-based practice implementation
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Methodology Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <Calculate sx={{ fontSize: 48, color: '#153C7F', mr: 2 }} />
            <Typography variant="h2" sx={{ color: '#153C7F', fontWeight: 700 }}>
              How We Calculate Time Savings
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333' }}>
            Our methodology is based on real facility data and validated through extensive pilot programs
          </Typography>
        </Box>

        <Grid container spacing={6}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4, 
                height: '100%',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(21,60,127,0.1)'
              }}
            >
              <Typography variant="h4" sx={{ color: '#153C7F', mb: 4, fontWeight: 700 }}>
                📊 Baseline Measurement
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#153C7F' }} />
                  <Typography variant="body1" sx={{ lineHeight: 1.6, fontSize: '1.1rem' }}>
                    Time tracking of current manual processes
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#153C7F' }} />
                  <Typography variant="body1" sx={{ lineHeight: 1.6, fontSize: '1.1rem' }}>
                    Staff surveys and interviews
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#153C7F' }} />
                  <Typography variant="body1" sx={{ lineHeight: 1.6, fontSize: '1.1rem' }}>
                    Process mapping and analysis
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#153C7F' }} />
                  <Typography variant="body1" sx={{ lineHeight: 1.6, fontSize: '1.1rem' }}>
                    Historical data review
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4, 
                height: '100%',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(21,60,127,0.1)'
              }}
            >
              <Typography variant="h4" sx={{ color: '#153C7F', mb: 4, fontWeight: 700 }}>
                ⚡ Automation Impact
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#28a745' }} />
                  <Typography variant="body1" sx={{ lineHeight: 1.6, fontSize: '1.1rem' }}>
                    Automated workflow analysis
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#28a745' }} />
                  <Typography variant="body1" sx={{ lineHeight: 1.6, fontSize: '1.1rem' }}>
                    Pilot program results
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#28a745' }} />
                  <Typography variant="body1" sx={{ lineHeight: 1.6, fontSize: '1.1rem' }}>
                    Comparative facility studies
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#28a745' }} />
                  <Typography variant="body1" sx={{ lineHeight: 1.6, fontSize: '1.1rem' }}>
                    Continuous improvement tracking
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ 
        py: 12, 
        background: 'linear-gradient(135deg, #153C7F 0%, #1E4A8C 100%)',
        color: 'white',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)',
          backgroundSize: '50px 50px'
        }} />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ mb: 4, fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' } }}>
            Start Saving Time Today
          </Typography>
          <Typography variant="h5" sx={{ mb: 6, opacity: 0.9, lineHeight: 1.6 }}>
            Join facilities already experiencing these significant time savings with CareAscendium
          </Typography>
          <Link href="/contact" style={{ textDecoration: 'none' }}>
            <Button 
              variant="contained" 
              size="large"
              startIcon={<TrendingUp />}
              sx={{ 
                bgcolor: 'white',
                color: '#153C7F',
                px: 6,
                py: 3,
                fontSize: '1.2rem',
                fontWeight: 700,
                borderRadius: 3,
                textTransform: 'none',
                boxShadow: '0 4px 15px rgba(255,255,255,0.3)',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  boxShadow: '0 6px 20px rgba(255,255,255,0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Calculate Your Savings
            </Button>
          </Link>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
} 